import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import { Divider } from '@creditsights/styleguide-web';
import styles from './CoreScoreWidget.module.scss';
import ScaleIndicator from './ScaleIndicator/ScaleIndicator';
import CoreIcon from './Static/CoreIcon';
import { CORE, STRATEGIC, MODERATE_RISK, SPECULATIVE } from './CoreScore.constants';
import StrategicIcon from './Static/StrategicIcon';
import ModerateRiskIcon from './Static/ModerateRiskIcon';
import SpeculativeIcon from './Static/SpeculativeIcon';
import ScoreIcon from './Static/ScoreIcon';
import OutlookIcon from './Static/OutlookIcon';
import { CoreScoreWidgetProps, SubFactorProps } from '../../../models/Score';
import { fetchScoresCategory } from '../../../utils/scoreUtils';

const cx = classNames.bind(styles);

const CoreScoreContent = ({
  scoreNumber,
  outlookNumber,
  riskFactor,
  nonRiskFactor,
  lastChanged,
  lastReviewed,
}: CoreScoreWidgetProps): React.ReactElement => {
  const [score, setScore] = useState<string>('');
  const [outlook, setOutlook] = useState<string>('');

  useEffect(() => {
    if (scoreNumber && outlookNumber) {
      const scoresCategory = fetchScoresCategory(scoreNumber, outlookNumber);
      setScore(scoresCategory.score);
      setOutlook(scoresCategory.outlook);
    }
  }, []);

  const renderScaleWithHeader = (headings: Array<string>, scoreFactors: SubFactorProps[] | undefined) => {
    return (
      <div className={cx('risk-subfactor-wrapper')}>
        <div className={cx('scale-heading-wrapper')}>
          <div className={cx('scale-heading')}>
            <div className={cx('scale-heading-child')}>
              <p>{headings[0]}</p>
            </div>
            <div className={cx('scale-heading-child')}>
              <p>{headings[1]}</p>
            </div>
            <div className={cx('scale-heading-child')}>
              <p>{headings[2]}</p>
            </div>
          </div>
        </div>

        {scoreFactors?.map((element) => {
          return <ScaleIndicator label={element.label} score={element.score} outlook={element.outlook} />;
        })}
      </div>
    );
  };

  const renderCumulativeIndicator = (cumulativeValue: string, cumulativeHeading: string) => {
    return (
      <div
        className={cx(
          'cumulative-indicator',
          { 'background-green': cumulativeValue === CORE || cumulativeValue === STRATEGIC },
          { 'background-yellow': cumulativeValue === MODERATE_RISK },
          { 'background-red': cumulativeValue === SPECULATIVE }
        )}
      >
        <p className={cx('heading')}>{cumulativeHeading}:</p>
        <div className={cx('score-value')}>
          {cumulativeValue === CORE && <CoreIcon />}
          {cumulativeValue === STRATEGIC && <StrategicIcon />}
          {cumulativeValue === MODERATE_RISK && <ModerateRiskIcon />}
          {cumulativeValue === SPECULATIVE && <SpeculativeIcon />}
          <p>{cumulativeValue}</p>
        </div>
      </div>
    );
  };

  return (
    <div className={cx('print-body')}>
      <div className={cx('score-values-parent')}>
        {renderCumulativeIndicator(score, 'Score')}
        {renderCumulativeIndicator(outlook, 'Outlook')}
      </div>

      <Divider vertical={false} className={cx('print-divider')} />

      <div className={cx('subfactor-heading')}>
        <p>Subfactors</p>
        <div className={cx('icon')}>
          <div className={cx('icon-child')}>
            <div className={cx('svg-wrapper')}>
              <ScoreIcon />
            </div>
            <p className={cx('dot-indicator')}>Score</p>
          </div>
          <div className={cx('icon-child')}>
            <div className={cx('svg-wrapper')}>
              <OutlookIcon />
            </div>
            <p className={cx('dot-indicator')}>Outlook</p>
          </div>
        </div>
      </div>

      {renderScaleWithHeader(['High', 'Moderate', 'Low'], riskFactor)}
      <Divider vertical={false} className={cx('print-divider')} />
      {renderScaleWithHeader(['Weak', 'Average', 'Strong'], nonRiskFactor)}

      <div className={cx('dates')}>
        <p>Score last changed: {lastChanged}</p>
        <p>Score last reviewed: {lastReviewed}</p>
      </div>
    </div>
  );
};

export default CoreScoreContent;
